import { useState, useEffect, useMemo } from "react";
import { Modal, Checkbox } from "antd";
import {DataStore} from '@reams/elias-store';
import { Helmet } from "react-helmet";
import {
  WarningOutlined
} from "@ant-design/icons";
import ItemTableGrid from "components/ItemTableGrid/ItemTableGrid";
import { EditIcon, DeleteIcon } from "components/Icons";
import ItemDrawer from "components/ItemDrawer/ItemDrawer";
import FilterHeader from "components/FilterHeader/FilterHeader";
import {Asset} from "models";
import Spinner from "components/Spinner/Spinner";
import { visibleActions } from "components/ButtonPair/ActionsDropdown";
import { ALL_ASSETS_COLUMN, useRenderGrid } from "../../helpers/asset/assetTableHelpers"
import { fetchFilterDescription, filterItem } from "helpers/filters/itemFilterHelpers";
import {
  buildFilterTreeFromHierarchy,
  buildFilterableTreeByFacet,
  buildLocationFilterableTree,
  filterableOptions,
  treeObject,
  getEnhancedValidationStatus
} from "helpers/filters/assetFilterHelpers";
import _ from "lodash";
import { getSessionStorage, setSessionStorage, getLocalStorage, setLocalStorage } from "helpers/browserStorage";
import { logUserEvent } from "helpers/userEventLogger";
import { useOnlineContext } from "helpers/contexts/OnlineContext/OnlineContext";
import { useEntityData } from "EntityDataContext/EntityDataContext";
import { getAssetDefinition } from "helpers/hierarchyHelpers";

export default function AllAssetsDetailsPage({
  pageHeader,
}) {
  const PAGE_KEY = "AllAssetsListing";
  const DEFAULT_SORT = "updatedAt";
  const DEFAULT_SORT_ORDER = "descend";

  const hierarchy = window.hierarchy;

  const [sortBy, setSortBy] = useState(getLocalStorage(PAGE_KEY, "SortBy") || DEFAULT_SORT);
  const [sortOrder, setSortOrder] = useState(getLocalStorage(PAGE_KEY, "SortOrder") || DEFAULT_SORT_ORDER);
  const [selectedAsset, setSelectedAsset] = useState();
  const [parents, setParents] = useState();
  const [filters, setFilters] = useState([]);

  const { facilities, assets, spaces, floors, userData: { email }, tenantData: { showAssetValidationStatus = false } = {}, syncStatusHook: { imagesToUploadCount } } = useEntityData();

  const { isOnline } = useOnlineContext();

  useEffect(() => {
    const sessionSortBy = getLocalStorage(PAGE_KEY, "SortBy");
    const sessionSortOrder = getLocalStorage(PAGE_KEY, "SortOrder");

    if (sortBy !== sessionSortBy) {
      setLocalStorage(PAGE_KEY, "SortBy", sortBy);
    }

    if (sortOrder !== sessionSortOrder) {
      setLocalStorage(PAGE_KEY, "SortOrder", sortOrder);
    }
  }, [sortBy, sortOrder])

  useEffect(() => {
    const currentSessionFilters = getSessionStorage(PAGE_KEY, "Filters") || [];

    if (
      currentSessionFilters.length && 
      Object.keys(floors).length && 
      Object.keys(spaces).length &&
      !filters.length
    ) {
      currentSessionFilters.map((f) => setFiltersNode(f));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floors, spaces])
  
  const setFiltersNode = (val) => {
    let desc = val;

    if (desc !== 'validation-status_Not Validated') {
      desc = fetchFilterDescription({filter: desc, facets: window.facets, facilities, floors: floors.items, spaces: spaces.items});
    } else {
      desc = 'Validation Status: Not Validated';
    }
    
    const x = { id: val, description: desc };

    setFilters((old) => {
      if (old.find((o) => o.id === x.id)) {
        return [...old];
      }
      return [...old, x];
    });
  };

  const onFiltersChange = (node) => {
    setSessionStorage(PAGE_KEY, "Filters", node);

    // from select fields
    if (Array.isArray(node)) {
      node.map((node) => setFiltersNode(node));
      return;
    }

    // from option fields
    setFiltersNode(node.value);
  };

  const onFilterClose = (filter) => {
    const newFilterKeys = (getSessionStorage(PAGE_KEY, "Filters") || []).filter(f => f !== filter);

    setSessionStorage(PAGE_KEY, "Filters", newFilterKeys);

    setFilters((old) => {
      return old.filter((f) => f.id !== filter);
    });
  };

  const allAssets = useMemo(() => Object.values(assets.items)?.map((asset) => {
      const assetDefinition = getAssetDefinition(
        asset.assetType?.eliasID,
        hierarchy
        );

      return {
        ...asset,
        name: assetDefinition?.knownAs || asset.assetType?.description,
        barcode: `${asset.facets?.barcode || ''} ${asset.notes?.barcode?.status ? `[${asset.notes?.barcode?.status}]` : ''}` ,
        quantity: asset.facets?.quantity,
        installDate: asset.facets && asset.facets["install-date"],
        condition: asset.facets?.condition,
        manufacturer: asset.facets?.manufacturer,
      };
    }), [assets]);

  const filterTreeData = useMemo(() => {
    // search through facets.json and find appropriate facets
    const filterableFacets = structuredClone(window.facets.filter((i) => i.filterable));

    const validationStatusFilterIndex = filterableFacets.findIndex(f => f.facetId === 'uf46');

    if (showAssetValidationStatus && validationStatusFilterIndex > -1) {
      const enhancedValidationStatus = getEnhancedValidationStatus(filterableFacets[validationStatusFilterIndex]);
        
      filterableFacets[validationStatusFilterIndex] = { ...enhancedValidationStatus };
    } else {
      // remove Validation Status from filters
      filterableFacets.splice(validationStatusFilterIndex, 1);
    }

    const nonRatingGroupFacets = filterableFacets.filter(i => !i.ratingGroup);
    const ratingGroupFacets = filterableFacets.filter(i => i.ratingGroup);

    const nonRGF = nonRatingGroupFacets.map((i) => {
      return buildFilterableTreeByFacet(allAssets, i, onFiltersChange, filters, onFilterClose);
    })

    const uniqueRGs = _.uniq(ratingGroupFacets.map((f) => f.ratingGroup))

    const rgTrees = uniqueRGs.map(rg => treeObject(rg, rg, ratingGroupFacets.filter((rgf) => rgf.ratingGroup === rg).map((i) => {
      return buildFilterableTreeByFacet(allAssets, i, onFiltersChange, filters, onFilterClose);
    }).filter((f) => f))).filter((rg) => rg.children && rg.children.length > 0)

    const RGF = rgTrees.length > 0 ? [treeObject('Rating', 'Rating', rgTrees)] : []

    const LFT = treeObject(
      'Location', 
      'Location', 
      buildLocationFilterableTree(
        assets,
        facilities, 
        floors, 
        spaces
      )
    )
    
    // build System filter
    const treeData = buildFilterTreeFromHierarchy(window.hierarchy, allAssets)
    const SF = treeObject('System', 'System', treeData)

    // build Asset Class filter
    const ACF = treeObject('Asset Class', 'Asset Class', filterableOptions(allAssets, { key: 'Asset Class' }))

    return [SF, LFT, ACF, ...nonRGF, ...RGF];
  }, [allAssets, filters]);

  const fetchParents = async (selectedAsset) => {
    const space = spaces.items[selectedAsset.spaceId]
    const facility = facilities[selectedAsset.facilityId]
    const floor = floors.items[selectedAsset.floorId]
    setParents({ space, facility, floor });
  };

  useEffect(() => {
    if (!selectedAsset) return;

    fetchParents(selectedAsset);
  }, [selectedAsset]);


  const enhancedAssets = useMemo(() => {
    const filteredAssets = filterItem(
      allAssets, 
      filters, 
      window.facets
    )

    const enhancedAssetList = filteredAssets.map(f => ({
      ...f,
      location: {
        facilityName: facilities?.[f.facilityId]?.name || '',
        floorName: floors.items?.[f.floorId]?.name || '',
        spaceName: spaces.items?.[f.spaceId]?.name || ''
      }
    }))
    
    return enhancedAssetList
  }, [allAssets, filters, facilities, floors, spaces]);

  const actions = (asset) => {
    return [
      {
        label: "Edit",
        key: "edit",
        onClick: () => {
          const { id } = asset;

          logUserEvent({
            user: email,
            eventType: `Edit asset ${id} clicked on assets listing via All Assets view`,
            imageOutboxSize: imagesToUploadCount
          }, isOnline);
          
          setSelectedAsset(asset);
        },
        icon: <EditIcon />,
      },
      {
        label: "Delete",
        key: "delete",
        onClick: () => confirmDeleteAsset(asset),
        icon: <DeleteIcon />,
      },
    ];
  };

  const onConfirmDelete = (checked, modal) => {
    modal.update((prevConfig) => ({
      ...prevConfig,
      okButtonProps: {
        disabled: !checked
      }
    }));
  }

  async function confirmDeleteAsset(asset) {
    const modal = Modal.confirm({
      className: "delete-confirmation-modal",
      title: `Delete Asset - ${asset.name}?`,
      content:  
      <>
        <p>Are you sure you want to delete <span className="delete-confirmation-assertive-text">{asset.name}{asset?.notes?.description ? ' - ' + asset?.notes?.description : null}</span>?</p>
        <Checkbox
          className="delete-confirmation-checkbox" 
          onChange={(event) => onConfirmDelete(event.target.checked, modal)}
        >
          <span className="delete-confirmation-assertive-text">Yes, remove asset</span>
        </Checkbox>
      </>,
      icon: <WarningOutlined className="delete-confirmation-warning-icon"/>,
      okText: "Confirm",
      cancelText: "Cancel",
      width: 600,
      okButtonProps: {
        disabled: true
      },
      onOk: async () => {
        const itemToDelete = await DataStore.query(Asset, asset.id);
        
        await DataStore.delete(itemToDelete);

        logUserEvent({
          user: email,
          eventType: `Asset ${asset.id} deleted via Delete Asset modal on All Assets view`,
          imageOutboxSize: imagesToUploadCount
        }, isOnline);
      },
    });
  }

  const columnTitles = {
    installDate: "Installed",
    barcode: "Asset Id",
    updatedAt: "Last Edited",
    quantity: 'QTY',
    validated: 'Status'
  };

  const tableColumnTitleProps = {
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    columnTitles,
  };

  const ASSET_COLUMNS = ALL_ASSETS_COLUMN({
    showAssetValidationStatus,
    tableColumnTitleProps,
    sortBy,
    sortOrder,
    actions: (asset) => {
      const enhancedAsset = {
        ...asset,
        facilityName: facilities?.[asset.facilityId]?.name || null,
        floorName: floors.items?.[asset.floorName]?.name || null,
        spaceName: spaces.items?.[asset.spaceId]?.name || null
      }

      return visibleActions(actions(enhancedAsset))
    }
  })

  if (!allAssets) {
    return <Spinner message="We are fetching and processing your data..." />;
  }

  return (
    <div className="space-details-page">
      <Helmet title={"All Assets"} />
      {pageHeader}
      <div className="content-header">
        <FilterHeader
          filterTreeData={filterTreeData}
          filterOnChange={(change) => {
            onFiltersChange(change);
          }}
          filterOnCloseTag={(closeTag) => onFilterClose(closeTag)}
          filterValue={filters}
          sortByOptions={ASSET_COLUMNS}
          {...tableColumnTitleProps}
        />
      </div>

      <ItemTableGrid
        title="All Assets"
        items={enhancedAssets}
        columns={ASSET_COLUMNS}
        onClick={(a) => setSelectedAsset(a)}
        actions={actions}
        render={useRenderGrid}
      />

      {selectedAsset && parents && (
        <ItemDrawer
          onClose={() => {
            setSelectedAsset(false);
            setParents();
          }}
          visible={selectedAsset}
          item={selectedAsset}
          itemType="asset"
          parents={parents}
        />
      )}
    </div>
  );
}
